<template>
  <div
      v-bind="$attrs"
      :class="['custom-select', inputClass, { 'is-invalid': error }]"
      :tabindex="tabindex"
      @blur="handleBlur"
      @keydown="handleKeydown"
      ref="selectContainer"
  >
    <div
        class="selected"
        :class="{ open: open, disabled: disabled }"
        @click="toggleDropdown"
        role="combobox"
        :aria-expanded="open"
        :aria-haspopup="true"
        :aria-labelledby="ariaLabelledby"
    >
      <span class="select2-selection__rendered">
        {{ selectedLabel || placeholder }}
      </span>
      <span class="select2-selection__arrow" role="presentation">
        <i class='bx bxs-down-arrow'></i>
      </span>
    </div>

    <div
        class="items"
        :class="{ selectHide: !open }"
        role="listbox"
    >
      <!-- Búsqueda opcional -->
      <div v-if="searchable && open" class="search-box">
        <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="form-control form-control-sm"
            placeholder="Buscar..."
            @click.stop
        />
      </div>

      <!-- Opciones filtradas -->
      <div
          v-for="option in filteredOptions"
          :key="option.value"
          :class="['option-item', { selected: option.value === modelValue }]"
          @click="selectOption(option)"
          @mouseenter="highlightedIndex = filteredOptions.indexOf(option)"
          role="option"
          :aria-selected="option.value === modelValue"
      >
        <slot name="option" :option="option">
          {{ option.label }}
        </slot>
      </div>

      <!-- Mensaje cuando no hay opciones -->
      <div v-if="filteredOptions.length === 0" class="no-options">
        No hay opciones disponibles
      </div>
    </div>

    <!-- Mensaje de error -->
    <div v-if="error" class="invalid-feedback d-block">
      {{ error }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'SelectCustom',
  inheritAttrs: false,

  props: {
    modelValue: {
      required: true,
    },
    options: {
      type: Array,
      required: true,
      validator: (options) => {
        return options.every(opt =>
            typeof opt === 'object' &&
            'value' in opt &&
            'label' in opt
        );
      }
    },
    placeholder: {
      type: String,
      default: 'Seleccionar...',
    },
    tabindex: {
      type: Number,
      default: 0,
    },
    inputClass: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
    ariaLabelledby: {
      type: String,
      default: '',
    },
  },

  emits: ['update:modelValue', 'change'],

  data() {
    return {
      open: false,
      searchQuery: '',
      highlightedIndex: -1,
    };
  },

  computed: {
    selectedLabel() {
      const selected = this.options.find(opt => opt.value === this.modelValue);
      return selected?.label || null;
    },

    filteredOptions() {
      if (!this.searchQuery) {
        return this.options;
      }

      const query = this.searchQuery.toLowerCase();
      return this.options.filter(option =>
          option.label.toLowerCase().includes(query)
      );
    },
  },

  watch: {
    open(isOpen) {
      if (isOpen && this.searchable) {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
        });
      }

      if (isOpen) {
        document.addEventListener('click', this.handleClickOutside);
      } else {
        document.removeEventListener('click', this.handleClickOutside);
        this.searchQuery = '';
      }
    },
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  methods: {
    toggleDropdown() {
      if (!this.disabled) {
        this.open = !this.open;
      }
    },

    selectOption(option) {
      this.$emit('update:modelValue', option.value);
      this.$emit('change', option.value);
      this.open = false;
    },

    handleBlur(event) {
      // No cerrar si el blur es hacia un elemento hijo
      if (!this.$refs.selectContainer?.contains(event.relatedTarget)) {
        this.open = false;
      }
    },

    handleClickOutside(event) {
      if (!this.$refs.selectContainer?.contains(event.target)) {
        this.open = false;
      }
    },

    handleKeydown(event) {
      if (this.disabled) return;

      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (!this.open) {
            this.open = true;
          } else if (this.highlightedIndex >= 0) {
            this.selectOption(this.filteredOptions[this.highlightedIndex]);
          }
          break;

        case 'Escape':
          event.preventDefault();
          this.open = false;
          break;

        case 'ArrowDown':
          event.preventDefault();
          if (!this.open) {
            this.open = true;
          } else {
            this.highlightedIndex = Math.min(
                this.highlightedIndex + 1,
                this.filteredOptions.length - 1
            );
          }
          break;

        case 'ArrowUp':
          event.preventDefault();
          if (this.open) {
            this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
          }
          break;

        case 'Home':
          event.preventDefault();
          if (this.open) {
            this.highlightedIndex = 0;
          }
          break;

        case 'End':
          event.preventDefault();
          if (this.open) {
            this.highlightedIndex = this.filteredOptions.length - 1;
          }
          break;
      }
    },
  },
};
</script>

<style scoped>
.custom-select {
  position: relative;
  text-align: left;
  outline: none;
  line-height: 37px;
  min-width: 80px;
  flex: 1 1 auto;
  border: 1px solid #d9dee3;
  border-radius: 0.375rem;
  background-color: #fff;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.custom-select:focus-within {
  border-color: #696cff;
  box-shadow: 0 0 0 0.2rem rgba(105, 108, 255, 0.25);
}

.custom-select.is-invalid {
  border-color: #dc3545;
}

.custom-select .selected {
  color: #697a8d;
  padding-left: 1em;
  cursor: pointer;
  user-select: none;
  min-height: 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.custom-select .selected.disabled {
  cursor: not-allowed;
  opacity: 0.6;
  background-color: #f5f5f5;
}

.custom-select .selected.open {
  border-radius: 6px 6px 0 0;
}

.custom-select .select2-selection__rendered {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  outline: 0 !important;
}

.custom-select .select2-selection__arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  transition: transform 0.2s;
}

.custom-select .selected.open .select2-selection__arrow {
  transform: rotate(180deg);
}

.custom-select .items {
  color: #697a8d;
  border-radius: 0 0 6px 6px;
  overflow-y: auto;
  max-height: 250px;
  border-right: 1px solid #d9dee3;
  border-left: 1px solid #d9dee3;
  border-bottom: 1px solid #d9dee3;
  position: absolute;
  background-color: #fff;
  left: 0;
  right: 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 9999;
}

.custom-select .search-box {
  padding: 8px;
  border-bottom: 1px solid #d9dee3;
  position: sticky;
  top: 0;
  background: #fff;
}

.custom-select .option-item {
  padding: 8px 16px;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s;
}

.custom-select .option-item:hover {
  background-color: #f0f0f0;
}

.custom-select .option-item.selected {
  background-color: #696cff;
  color: white;
}

.custom-select .no-options {
  padding: 12px 16px;
  color: #999;
  text-align: center;
  font-style: italic;
}

.selectHide {
  display: none;
}

.invalid-feedback {
  font-size: 0.875rem;
  color: #dc3545;
  margin-top: 0.25rem;
}
</style>
